import React, { createContext, useContext, useEffect, useState } from 'react';
import { useCredits } from './CreditContext';
import { API_BASE_URL, p3OrgConfig } from '@/config';
import { useAuth } from './AuthContext';

export interface WhiteLabelConfig {
  isEnabled: boolean;
  logoUrl: string;
  companyName: string;
  primaryColor?: string;
  secondaryColor?: string;
  isDarkTheme?: boolean;
  backgroundColor?: string;
  motto?: string;
  mottoPosition?: 'inline' | 'below';
}

interface WhiteLabelContextType {
  config: WhiteLabelConfig;
  loading: boolean;
  error: string | null;
}

const defaultConfig: WhiteLabelConfig = {
  isEnabled: false,
  logoUrl: '/Nomadic_Logo_Only_Latest.svg',
  companyName: 'NomadicAI',
  primaryColor: '#3E48CC',
  secondaryColor: '#27E6D6',
};

const WhiteLabelContext = createContext<WhiteLabelContextType | undefined>(undefined);

export const WhiteLabelProvider = ({ children }: { children: React.ReactNode }) => {
  const { profile } = useCredits();
  const { user, getToken } = useAuth();
  const [config, setConfig] = useState<WhiteLabelConfig>(defaultConfig);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchWhiteLabelConfig = async () => {
      // If user is not part of an organization, use default config
      if (!profile?.org_id || !user) {
        setConfig(defaultConfig);
        setLoading(false);
        return;
      }

      // Optimistic config: if we recognize this org, apply white label immediately
      // if (profile.org_id === 'org_86fc9717efd9') {
      //   setConfig({
      //     isEnabled: true,
      //     logoUrl: '/Urban Hawk Logo New.png',
      //     companyName: 'Urban Hawk',
      //     primaryColor: '#1F2937',
      //     secondaryColor: '#3B82F6',
      //   });
      // } else
      if (profile.org_id === 'org_atlas_pro') {
        setConfig({
          isEnabled: true,
          logoUrl: '/atlaspro/atlas_pro_logo.svg',
          companyName: 'Atlas Pro',
          primaryColor: '#2d4a47',
          secondaryColor: '#14b8a6',
          isDarkTheme: true,
          backgroundColor: '#1a2e2b',
        });
      }

      if (profile.org_id === p3OrgConfig.orgId) {
        setConfig(p3OrgConfig.config);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const token = await getToken();
        if (!token) {
          throw new Error('Authentication token not found');
        }

        const response = await fetch(`${API_BASE_URL}/organizations/current/metadata`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch organization data');
        }

        const orgData = await response.json();

        // Check if white labeling is enabled for this organization
        const isWhitelabelOrg = orgData.WhiteLabeling === true || /* profile?.org_id === 'org_86fc9717efd9' || */ profile?.org_id === 'org_atlas_pro';
        if (isWhitelabelOrg) {
          // Configure based on org
          if (profile?.org_id === 'org_atlas_pro') {
            setConfig({
              isEnabled: true,
              logoUrl: '/atlaspro/atlas_pro_logo.svg',
              companyName: 'Atlas Pro',
              primaryColor: '#2d4a47',
              secondaryColor: '#14b8a6',
              isDarkTheme: true,
              backgroundColor: '#1a2e2b',
            });
          } else {
            // Default to Urban Hawk config for other whitelabel orgs
            setConfig({
              isEnabled: true,
              logoUrl: '/Urban Hawk Logo New.png',
              companyName: 'Urban Hawk',
              primaryColor: '#1F2937',
              secondaryColor: '#3B82F6',
            });
          }
        } else {
          setConfig(defaultConfig);
        }
      } catch (err: any) {
        console.error('Error fetching white label config:', err);
        setError(err.message || 'Failed to fetch white label configuration');
        setConfig(defaultConfig);
      } finally {
        setLoading(false);
      }
    };

    fetchWhiteLabelConfig();
  }, [profile?.org_id, user, getToken]);

  return (
    <WhiteLabelContext.Provider
      value={{
        config,
        loading,
        error,
      }}
    >
      {children}
    </WhiteLabelContext.Provider>
  );
};

export const useWhiteLabel = () => {
  const context = useContext(WhiteLabelContext);
  if (!context) {
    throw new Error('useWhiteLabel must be used within a WhiteLabelProvider');
  }
  return context;
};
